/**
 * `StammdatenSidebar` — the shared Stammdaten module sidebar (1:1 with the
 * design map's Stammdaten section). Every Stammdaten sub-view (Kontakte,
 * Liegenschaften, Portal, Serienbriefe, Versammlungen) renders this same
 * sidebar; the `active` key highlights the matching row. The "Wiederkehrend"
 * group (Mieterhöhungen / Wartungen / Zähler) is inert in this mock.
 */
import {
  Building2,
  CalendarClock,
  Gauge,
  Mail,
  Megaphone,
  Repeat,
  TrendingUp,
  Users,
  Wrench,
} from "lucide-react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { EyebrowLabel } from "./hero-primitives";
import { SidebarRow } from "./hero-primitives-app";
import { H, MODULE_HEIGHT, SIDEBAR_WIDTH } from "./hero-tokens";
import type { HeroModuleKey } from "./hero-types";

const ICON = 16;

/** Message key inside `marketing.heroStammdatenSidebar` for a row label. */
type SidebarLabelKey =
  | "kontakte"
  | "liegenschaften"
  | "mailings"
  | "portal"
  | "versammlungen"
  | "mieterhoehungen"
  | "wartungen"
  | "zaehler";

interface NavItem {
  readonly key: HeroModuleKey;
  readonly labelKey: SidebarLabelKey;
  readonly icon: ReactNode;
}

/** Stammdaten group — switches the active module via `setActive`. */
const STAMMDATEN_ITEMS: readonly NavItem[] = [
  {
    key: "kontakte",
    labelKey: "kontakte",
    icon: <Users width={ICON} height={ICON} />,
  },
  {
    key: "liegenschaften",
    labelKey: "liegenschaften",
    icon: <Building2 width={ICON} height={ICON} />,
  },
];

/** Kommunikation group — also switches the active module. */
const KOMMUNIKATION_ITEMS: readonly NavItem[] = [
  {
    key: "serienbrief",
    labelKey: "mailings",
    icon: <Mail width={ICON} height={ICON} />,
  },
  {
    key: "portal",
    labelKey: "portal",
    icon: <Megaphone width={ICON} height={ICON} />,
  },
  {
    key: "etv",
    labelKey: "versammlungen",
    icon: <CalendarClock width={ICON} height={ICON} />,
  },
];

/** Wiederkehrend group — inert decorative rows (no `setActive`). */
const WIEDERKEHREND_ITEMS: readonly {
  readonly labelKey: SidebarLabelKey;
  readonly icon: ReactNode;
}[] = [
  {
    labelKey: "mieterhoehungen",
    icon: <TrendingUp width={ICON} height={ICON} />,
  },
  { labelKey: "wartungen", icon: <Wrench width={ICON} height={ICON} /> },
  { labelKey: "zaehler", icon: <Gauge width={ICON} height={ICON} /> },
];

function Group(props: {
  readonly label: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ padding: "0 9px 6px" }}>
        <EyebrowLabel>{props.label}</EyebrowLabel>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {props.children}
      </div>
    </div>
  );
}

export function StammdatenSidebar(props: {
  readonly active: HeroModuleKey;
  readonly setActive: (key: HeroModuleKey) => void;
}): ReactNode {
  const t = useTranslations("marketing.heroStammdatenSidebar");
  return (
    <div
      style={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        height: MODULE_HEIGHT,
        boxSizing: "border-box",
        borderRight: "1px solid #EEF0F3",
        background: "#FBFBFC",
        padding: "16px 12px",
        overflowY: "auto",
      }}
    >
      <Group label={t("stammdaten")}>
        {STAMMDATEN_ITEMS.map((item) => (
          <SidebarRow
            key={item.key}
            icon={item.icon}
            label={t(item.labelKey)}
            active={props.active === item.key}
            onClick={() => props.setActive(item.key)}
          />
        ))}
      </Group>

      <Group label={t("kommunikation")}>
        {KOMMUNIKATION_ITEMS.map((item) => (
          <SidebarRow
            key={item.key}
            icon={item.icon}
            label={t(item.labelKey)}
            active={props.active === item.key}
            onClick={() => props.setActive(item.key)}
          />
        ))}
      </Group>

      <Group label={t("wiederkehrend")}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "0 9px 4px",
            color: H.inkDim,
          }}
        >
          <Repeat width={12} height={12} />
        </div>
        {WIEDERKEHREND_ITEMS.map((item) => (
          <SidebarRow
            key={item.labelKey}
            icon={item.icon}
            label={t(item.labelKey)}
          />
        ))}
      </Group>
    </div>
  );
}
